import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MediaScaleMode, ScreenRotationDegrees } from '@ghostscreen/contracts';
import type { DisplayPreviewSlide } from './DisplayPreviewCarousel';

type DisplaySetupPreviewProps = {
  slides: DisplayPreviewSlide[];
  scaleMode: MediaScaleMode;
  rotationDegrees: ScreenRotationDegrees;
  screenName: string;
  isPending?: boolean;
};

/**
 * Approximate TV mock for the manage dialog — shows draft playlist media
 * with Fit/Fill and orientation before the operator clicks Update.
 * Operators flip slides manually (prev / next / dots); no auto-advance.
 */
export function DisplaySetupPreview({
  slides,
  scaleMode,
  rotationDegrees,
  screenName,
  isPending = false,
}: DisplaySetupPreviewProps) {
  const { t } = useTranslation('displays');
  const [index, setIndex] = useState(0);
  const portrait = rotationDegrees === 90 || rotationDegrees === 270;
  const objectFit = scaleMode === 'fill' ? 'object-cover' : 'object-contain';
  const canNavigate = slides.length > 1;

  useEffect(() => {
    setIndex(0);
  }, [slides]);

  useEffect(() => {
    if (index >= slides.length) setIndex(0);
  }, [slides.length, index]);

  const goTo = (next: number) => {
    if (slides.length === 0) return;
    const wrapped = ((next % slides.length) + slides.length) % slides.length;
    setIndex(wrapped);
  };

  const slide = slides[index] ?? slides[0];

  return (
    <div className="flex flex-col gap-3" data-testid="display-setup-preview">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="m-0 text-sm font-bold text-ink">{t('preview.title')}</h3>
        <p className="m-0 text-xs text-muted">{t('preview.draftNote')}</p>
      </div>

      <div className="flex items-center justify-center rounded-[14px] border border-line bg-panel2 p-3 sm:p-4">
        <div
          className={[
            'relative w-full overflow-hidden rounded-[14px] border-[6px] border-[#1a1a1a] bg-black shadow-md',
            portrait
              ? 'mx-auto max-h-[18rem] max-w-[10rem] aspect-[9/16] sm:max-h-[22rem] sm:max-w-[11.5rem]'
              : 'aspect-video max-h-[12rem] sm:max-h-[16rem]',
            rotationDegrees === 180 ? 'rotate-180' : '',
            rotationDegrees === 270 ? '-scale-x-100' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          data-rotation={rotationDegrees}
          data-scale={scaleMode}
          role="img"
          aria-label={
            slide
              ? t('preview.ariaWithSlide', {
                  name: screenName,
                  index: index + 1,
                  total: slides.length,
                })
              : t('preview.aria', { name: screenName })
          }
        >
          {isPending ? (
            <div className="flex h-full items-center justify-center px-3 text-center text-xs text-muted">
              {t('preview.loading')}
            </div>
          ) : !slide ? (
            <div className="flex h-full flex-col items-center justify-center gap-1 px-3 text-center">
              <span className="text-xs font-semibold text-signal">{t('preview.noMedia')}</span>
              <span className="text-[11px] text-muted">{t('preview.pickPlaylist')}</span>
            </div>
          ) : slide.kind === 'video' ? (
            <video
              key={slide.id}
              src={slide.src}
              className={`h-full w-full bg-black ${objectFit}`}
              muted
              autoPlay
              loop
              playsInline
              aria-hidden
            />
          ) : (
            <img
              key={slide.id}
              src={slide.src}
              alt=""
              className={`h-full w-full bg-black ${objectFit}`}
              draggable={false}
            />
          )}
        </div>
      </div>

      {canNavigate ? (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => goTo(index - 1)}
            className="rounded-[8px] border border-line px-3 py-1.5 text-xs font-semibold text-ink hover:border-[var(--gs-accent)]"
            aria-label={t('preview.prevAria')}
          >
            {t('preview.prev')}
          </button>
          <div
            className="flex items-center justify-center gap-1.5"
            role="tablist"
            aria-label={t('preview.slidesAria')}
          >
            {slides.map((item, i) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={t('preview.slideAria', { index: i + 1 })}
                onClick={() => goTo(i)}
                className={
                  i === index
                    ? 'h-2.5 w-2.5 rounded-full bg-signal'
                    : 'h-2.5 w-2.5 rounded-full bg-line hover:bg-muted'
                }
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => goTo(index + 1)}
            className="rounded-[8px] border border-line px-3 py-1.5 text-xs font-semibold text-ink hover:border-[var(--gs-accent)]"
            aria-label={t('preview.nextAria')}
          >
            {t('preview.next')}
          </button>
        </div>
      ) : null}

      {slides.length > 0 ? (
        <p className="m-0 text-center text-xs text-muted" data-testid="preview-slide-position">
          {t('preview.position', {
            index: Math.min(index + 1, slides.length),
            total: slides.length,
          })}
        </p>
      ) : null}

      <p className="m-0 text-xs text-muted">{t('preview.syncNote')}</p>
    </div>
  );
}

/** Compact thumbnail strip for the draft playlist picker. */
export function PlaylistThumbStrip({ slides }: { slides: DisplayPreviewSlide[] }) {
  const { t } = useTranslation('displays');
  if (slides.length === 0) {
    return (
      <p className="m-0 text-xs text-muted" data-testid="playlist-thumb-empty">
        {t('preview.thumbsEmpty')}
      </p>
    );
  }

  return (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="playlist-thumb-strip">
      {slides.slice(0, 8).map((slide) => (
        <div
          key={slide.id}
          className="h-14 w-14 shrink-0 overflow-hidden rounded-[8px] border border-line bg-black"
        >
          {slide.kind === 'video' ? (
            <video
              src={slide.src}
              className="h-full w-full object-cover"
              muted
              playsInline
              aria-hidden
            />
          ) : (
            <img src={slide.src} alt="" className="h-full w-full object-cover" draggable={false} />
          )}
        </div>
      ))}
      {slides.length > 8 ? (
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[8px] border border-dashed border-line text-xs font-semibold text-muted">
          {t('preview.overflow', { count: slides.length - 8 })}
        </div>
      ) : null}
    </div>
  );
}
